import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Grid from '@cloudscape-design/components/grid';
import Header from '@cloudscape-design/components/header';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { useApi } from '../hooks/useApi';

interface DocumentVersion {
  version: number;
  changed_by: string;
  summary: string;
  created_at: string;
  content_url: string;
}

export function DocumentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { apiFetch } = useApi();
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    loadDocument();
  }, [id]);

  async function loadDocument() {
    setIsLoading(true);
    try {
      // Load audit/version history (which includes doc metadata)
      const data = await apiFetch<{ document_id: string; versions: DocumentVersion[] }>(`/audit/changes/${id}`);
      setVersions(data.versions);
      // TODO: separate doc metadata endpoint
    } catch (err) {
      console.error('Failed to load document:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerify() {
    try {
      await apiFetch(`/documents/${id}/verify`, { method: 'POST' });
      alert('Document verified successfully');
      loadDocument();
    } catch (err) {
      alert('Failed to verify document');
    }
  }

  async function handleDeprecate() {
    const successorId = prompt('Enter successor document ID:');
    if (!successorId) return;
    try {
      await apiFetch(`/documents/${id}/deprecate`, { method: 'POST', body: { successor_id: successorId } });
      alert('Document deprecated');
      loadDocument();
    } catch (err) {
      alert('Failed to deprecate document');
    }
  }

  if (isLoading) {
    return (
      <Box textAlign="center" padding={{ vertical: 'xxl' }}>
        <Spinner size="large" />
      </Box>
    );
  }

  return (
    <Grid gridDefinition={[{ colspan: { default: 12, l: 8 } }, { colspan: { default: 12, l: 4 } }]}>
      {/* Main content area */}
      <SpaceBetween size="l">
        <Container header={<Header variant="h1">Document: {id?.slice(0, 8)}...</Header>}>
          <Box color="text-status-inactive">
            Content would be rendered here (markdown for technical users, rich text for non-technical).
          </Box>
          {/* TODO: Fetch and render actual content from S3 pre-signed URL */}
        </Container>

        {/* Version History */}
        <Container header={<Header variant="h2">Version History</Header>}>
          {versions.length === 0 ? (
            <Box color="text-status-inactive">No version history available.</Box>
          ) : (
            <SpaceBetween size="s">
              {versions.map((v) => (
                <Grid
                  key={v.version}
                  gridDefinition={[{ colspan: 2 }, { colspan: 8 }, { colspan: 2 }]}
                >
                  <Box variant="awsui-value-large" textAlign="center">
                    v{v.version}
                  </Box>
                  <SpaceBetween size="xxxs">
                    <Box variant="strong">{v.summary || 'Content updated'}</Box>
                    <Box variant="small" color="text-status-inactive">
                      by {v.changed_by} · {new Date(v.created_at).toLocaleDateString()}
                    </Box>
                  </SpaceBetween>
                  <Box textAlign="right">
                    {v.content_url && (
                      <Link href={v.content_url} external externalIconAriaLabel="Opens in a new tab">
                        View
                      </Link>
                    )}
                  </Box>
                </Grid>
              ))}
            </SpaceBetween>
          )}
        </Container>

        {/* Relationship Graph Placeholder */}
        <Container header={<Header variant="h2">Relationships</Header>}>
          <Box textAlign="center" padding={{ vertical: 'xxxl' }} color="text-status-inactive">
            D3.js force-directed graph visualization (Neptune query)
          </Box>
        </Container>
      </SpaceBetween>

      {/* Metadata Sidebar */}
      <SpaceBetween size="l">
        <Container header={<Header variant="h3">Document Metadata</Header>}>
          <KeyValuePairs
            columns={1}
            items={[
              { label: 'ID', value: <Box variant="code">{id}</Box> },
              { label: 'Versions', value: versions.length },
            ]}
          />
        </Container>

        {/* Actions */}
        <Container header={<Header variant="h3">Actions</Header>}>
          <SpaceBetween size="xs">
            <Button fullWidth iconName="check" onClick={handleVerify}>
              Verify as Current
            </Button>
            <Button fullWidth iconName="edit" onClick={() => navigate(`/author?edit=${id}`)}>
              Edit
            </Button>
            <Button fullWidth iconName="status-warning" onClick={handleDeprecate}>
              Deprecate
            </Button>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Grid>
  );
}
